// Turns the FixedFilter a chart click would have applied into the FOCUS the
// agent reads. Pure: the same drill-down shapes the drill-down builders and the
// dashboard click menu already produce are the input, so every entry point
// ("Explain this value" in the menu, the picker on a report, a matrix cell)
// describes the clicked value identically.

import filterTypeCheckers from 'components/FilterPlateForm/filterTypeCheckers';
import {
  InsightsViewDrillTarget,
  InsightsViewFocus,
} from 'contextProviders/InsightsContentProvider/viewTypes';
import { getFieldLabel } from 'hooks/useDrillDownFieldName';
import isV5ChartDef from 'types/visTypeCheckers/isV5ChartDef';

import { BaseViewsLookup, toClause } from '../insightsViewGadget';

// The member as the user saw it. Mirrors DashboardContextMenu's toLabel so the
// menu row and the question agree on the wording.
export const describeDrillValue = (drillDown: FixedFilter): string => {
  if (filterTypeCheckers.isTextFilter(drillDown)) {
    if (drillDown.keywordsExists === false) {
      return '(Blank)';
    }
    return drillDown.keywordValues[0] ?? '';
  }
  if (filterTypeCheckers.isDateFilter(drillDown)) {
    return drillDown.dateRangeValues?.[0]?.label ?? '';
  }
  if (filterTypeCheckers.isNumberFilter(drillDown)) {
    const range = drillDown.rangeValue;
    if (!range) {
      return '';
    }
    return range.valueB !== undefined
      ? `${range.operator} ${range.valueA} and ${range.valueB}`
      : `${range.operator} ${range.valueA}`;
  }
  if (filterTypeCheckers.isBooleanFilter(drillDown)) {
    return `${drillDown.field} is ${drillDown.booleanValue}`;
  }
  return '';
};

// The raw member the digest files its row under: the keyword term for a text
// dimension, the ISO bucket start for a trend, the label otherwise.
const rawValueOf = (
  drillDown: FixedFilter,
  label: string,
): string | number | boolean => {
  if (filterTypeCheckers.isTextFilter(drillDown)) {
    return drillDown.keywordsExists === false
      ? '(Blank)'
      : drillDown.keywordValues[0] ?? label;
  }
  if (filterTypeCheckers.isDateFilter(drillDown)) {
    return drillDown.dateRangeValues?.[0]?.value.startDate ?? label;
  }
  if (
    filterTypeCheckers.isBooleanFilter(drillDown) &&
    drillDown.booleanValue !== undefined
  ) {
    return drillDown.booleanValue;
  }
  return label;
};

// Hour-of-day, day-of-week and range-bucket groupings are real on screen but
// have no metric-query equivalent, so a clause for them would send the agent
// after a filter it can't apply (the drill-down builders alert "Coming soon"
// for the same three cases).
const isClauseReproducible = ({
  drillDown,
  chartDef,
}: {
  drillDown: FixedFilter;
  chartDef?: VisualisationDefinition;
}): boolean => {
  if (!isV5ChartDef(chartDef)) {
    return true;
  }
  if (drillDown.field === 'key' && chartDef.trendByHourInterval) {
    return false;
  }
  if (drillDown.field === 'dayOfWeek' && chartDef.groupByDayOfWeek) {
    return false;
  }
  if (chartDef.dimensionA?.rangeInput) {
    return false;
  }
  return true;
};

const buildInsightsFocus = ({
  drillDown,
  term,
  metrics,
  chartDef,
  cardId,
  entityId,
  gadgetTitle,
  baseViews,
  drillTarget,
}: {
  // The filter the click would have applied; undefined for a single-figure
  // gadget where there is no member to point at.
  drillDown?: FixedFilter;
  // The clicked term as the chart reported it — the fallback label when the
  // drill-down carries none (a date bucket the chart couldn't resolve).
  term?: string;
  metrics: { id: string; name?: string }[];
  chartDef?: VisualisationDefinition;
  cardId: string;
  entityId: string;
  gadgetTitle: string;
  baseViews: BaseViewsLookup;
  drillTarget?: InsightsViewDrillTarget;
}): InsightsViewFocus => {
  const focus: InsightsViewFocus = {
    entityId,
    cardId,
    gadgetTitle,
    metrics,
    ...(term ? { term } : {}),
    ...(drillTarget ? { drillTarget } : {}),
  };
  if (!drillDown) {
    return focus;
  }

  const isDate = filterTypeCheckers.isDateFilter(drillDown);
  const label = describeDrillValue(drillDown) || term || '';

  if (!isDate) {
    focus.dimension = {
      field: drillDown.field,
      label: getFieldLabel({ field: drillDown.field, baseViews }),
    };
  }

  if (label) {
    // A clicked member is a single equality (or a blank bucket) → one clause.
    // Anything that maps to more than one (never expected for a drill click)
    // degrades to an unreproducible focus rather than guessing.
    const clauses = isClauseReproducible({ drillDown, chartDef })
      ? toClause(drillDown)
      : [];
    const clause = clauses.length === 1 ? clauses[0] : undefined;
    focus.value = {
      label,
      rawValue: rawValueOf(drillDown, label),
      ...(clause ? { clause } : {}),
    };
  }

  if (isDate) {
    const bucket = drillDown.dateRangeValues?.[0];
    if (bucket?.value.startDate && bucket.value.endDate) {
      focus.dateBucket = {
        startDate: bucket.value.startDate,
        endDate: bucket.value.endDate,
        ...(bucket.label ? { label: bucket.label } : {}),
      };
    }
  }

  return focus;
};

export default buildInsightsFocus;
